import React, { useState, useCallback } from 'react';
import {
  TextField,
  InputAdornment,
  Box,
  Tooltip,
  Fade,
  CircularProgress,
  Typography,
} from '@mui/material';
import { Search, Close, Info } from '@mui/icons-material';

interface SearchFieldProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  size?: 'small' | 'medium';
  disabled?: boolean;
  isLoading?: boolean;
  helperText?: string;
  showCharCount?: boolean;
  maxLength?: number;
  variant?: 'outlined' | 'filled' | 'standard';
  fullWidth?: boolean;
}

const SearchField: React.FC<SearchFieldProps> = ({
  value,
  onChange,
  placeholder = 'Search by keywords...',
  size = 'small',
  disabled = false,
  isLoading = false,
  helperText,
  showCharCount = false,
  maxLength,
  variant = 'outlined',
  fullWidth = true,
}) => {
  const [isFocused, setIsFocused] = useState(false);

  const handleClear = useCallback(() => {
    onChange('');
  }, [onChange]);

  return (
    <Box sx={{ width: fullWidth ? '100%' : 'auto' }}>
      {/* Label and Character Count */}
      {showCharCount && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 1,
          }}
        >
          {helperText && (
            <Tooltip title={helperText} arrow placement="top">
              <Typography
                variant="caption"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  color: '#666',
                  fontWeight: 500,
                }}
              >
                <Info sx={{ fontSize: '0.9rem' }} />
                {helperText}
              </Typography>
            </Tooltip>
          )}
          {maxLength && (
            <Typography
              variant="caption"
              sx={{
                color: value.length > maxLength * 0.8 ? '#ff9800' : '#999',
                fontWeight: 500,
              }}
            >
              {value.length} / {maxLength}
            </Typography>
          )}
        </Box>
      )}

      <TextField
        fullWidth={fullWidth}
        size={size}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(maxLength ? e.target.value.slice(0, maxLength) : e.target.value)}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        disabled={disabled || isLoading}
        autoComplete="off"
        variant={variant}
        inputProps={{
          maxLength: maxLength,
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              {isLoading ? (
                <Tooltip title="Searching..." arrow>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mr: 0.5,
                      color: '#1967d2',
                    }}
                  >
                    <CircularProgress size={18} thickness={4} />
                  </Box>
                </Tooltip>
              ) : (
                <Tooltip title="Search across all fields" arrow>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mr: 0.5,
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      color: isFocused || value ? '#1967d2' : '#b0bec5',
                      fontSize: '1.3rem',
                      fontWeight: 600,
                    }}
                  >
                    <Search />
                  </Box>
                </Tooltip>
              )}
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              <Fade in={!!value && !isLoading} timeout={200} unmountOnExit>
                <Box
                  component="button"
                  type="button"
                  onClick={handleClear}
                  onMouseDown={(e) => e.preventDefault()}
                  title="Clear search (Esc)"
                  aria-label="Clear search"
                  sx={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '6px 8px',
                    color: '#b0bec5',
                    marginRight: '-8px',
                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                    borderRadius: '6px',
                    '&:hover': {
                      color: '#1967d2',
                      backgroundColor: 'rgba(25, 103, 210, 0.1)',
                      transform: 'scale(1.1)',
                    },
                    '&:active': {
                      color: '#1565c0',
                      transform: 'scale(0.95)',
                    },
                  }}
                >
                  <Close sx={{ fontSize: '1.2rem', fontWeight: 700 }} />
                </Box>
              </Fade>
            </InputAdornment>
          ),
        }}
        sx={{
          width: '100%',
          '& .MuiOutlinedInput-root': {
            backgroundColor: isFocused ? '#ffffff' : '#f8f9fb',
            borderRadius: '10px',
            border: '1.5px solid',
            borderColor: isFocused ? '#1967d2' : '#e0e8f0',
            fontSize: '0.95rem',
            fontWeight: 500,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            boxShadow: isFocused
              ? '0 6px 24px rgba(25, 103, 210, 0.15), inset 0 1px 3px rgba(0, 0, 0, 0.05)'
              : '0 2px 8px rgba(0, 0, 0, 0.04)',
            '& fieldset': {
              border: 'none',
            },
            '&:hover': {
              borderColor: !isFocused ? '#d0d8e0' : '#1967d2',
              backgroundColor: !isFocused ? '#fafbfc' : '#ffffff',
              boxShadow: !isFocused
                ? '0 4px 12px rgba(0, 0, 0, 0.06)'
                : '0 6px 24px rgba(25, 103, 210, 0.15), inset 0 1px 3px rgba(0, 0, 0, 0.05)',
            },
            '&.Mui-focused': {
              borderColor: '#1967d2',
            },
            '&.Mui-disabled': {
              backgroundColor: '#f0f2f5',
              borderColor: '#e0e0e0',
              opacity: 0.7,
              '& input': {
                color: '#999',
                cursor: 'not-allowed',
              },
            },
            '& input': {
              padding: '12px 6px',
              color: '#1a1a1a',
              fontWeight: 500,
              letterSpacing: '0.2px',
              '&::placeholder': {
                color: '#b0bec5',
                opacity: 1,
                fontWeight: 400,
              },
              '&:-webkit-autofill': {
                WebkitBoxShadow: '0 0 0 1000px #ffffff inset',
                WebkitTextFillColor: '#1a1a1a',
              },
            },
          },
          '& .MuiInputAdornment-positionStart': {
            marginRight: '6px',
          },
          '& .MuiInputAdornment-positionEnd': {
            marginLeft: 0,
          },
        }}
      />

      {/* Helper Text */}
      {helperText && !showCharCount && (
        <Fade in={isFocused || !!value} timeout={300}>
          <Typography
            variant="caption"
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              mt: 0.75,
              color: '#999',
              fontWeight: 400,
            }}
          >
            <Info sx={{ fontSize: '0.8rem' }} />
            {helperText}
          </Typography>
        </Fade>
      )}
    </Box>
  );
};

export default SearchField;
